import { gql } from '@apollo/client'
import classNames from 'classnames'
import { useContext, useEffect, useRef } from 'react'

import { useChooseFrequencyLaterEvents } from '../../../hooks/use-choose-frequency-later-events'
import { getCardsByFrequency } from '../../../utils'
import { ChooseFrequencyLaterSteps } from '../../choose-frequency-later-steps'
import {
  ChooseFrequencyLaterStepsContent,
  StepContentWrapper,
} from '../../choose-frequency-later-steps/components'
import {
  ChooseFrequencyLaterContextProvider,
  useChooseOfferFrequencyLaterContext,
} from '../../choose-frequency-later-steps/context-provider'
import { ChooseFrequencyLaterModalSteps } from '../../choose-frequency-later-steps/types'
import { BoxLockContext } from '../../context'
import { ModalLock } from '../../dynamic-multiple-offers-switch-frequency-lock/components'
import switchFrequencyStyles from '../../dynamic-multiple-offers-switch-frequency-lock/dynamic-multiple-offers-switch-frequency-lock.module.scss'
import { PaywallLock } from '../../paywall-lock'
import {
  ChooseFrequencyLaterRedesignLock_OfferLockDataFragment,
  ChooseFrequencyLaterRedesignLock_ViewerFragment,
} from './__generated__/two-step-redesign.graphql'

export interface ChooseFrequencyLaterRedesignLockProps {
  offerLockData: ChooseFrequencyLaterRedesignLock_OfferLockDataFragment
  viewer: ChooseFrequencyLaterRedesignLock_ViewerFragment
}

export function ChooseFrequencyLaterRedesignLock({
  offerLockData,
  viewer,
}: ChooseFrequencyLaterRedesignLockProps) {
  const { componentType } = useContext(BoxLockContext)

  if (componentType === 'paywall') {
    return (
      <PaywallLock offerLockData={offerLockData}>
        <ChooseFrequencyLaterSteps
          offerLockData={offerLockData}
          viewer={viewer}
          componentType="paywall"
          compactHeader
        />
      </PaywallLock>
    )
  }

  return (
    <ChooseFrequencyLaterContextProvider
      offerLockData={offerLockData}
      componentType="modal"
      compactHeader
    >
      <ChooseFrequencyLaterRedesignModalContent offerLockData={offerLockData} viewer={viewer} />
    </ChooseFrequencyLaterContextProvider>
  )
}

function ChooseFrequencyLaterRedesignModalContent({
  offerLockData,
  viewer,
}: ChooseFrequencyLaterRedesignLockProps) {
  const { currentStep } = useChooseOfferFrequencyLaterContext()
  const { sendModalClosedEvent } = useChooseFrequencyLaterEvents()
  const isSecondStep = currentStep === ChooseFrequencyLaterModalSteps.SELECT_FREQUENCY

  const cards = offerLockData?.body?.cards || []
  const { monthlyCards, yearlyCards } = getCardsByFrequency(cards)
  const isOneOfferByFrequency = Math.max(monthlyCards.length, yearlyCards.length) === 1
  const isThreeOffersByFrequency = Math.max(monthlyCards.length, yearlyCards.length) === 3
  const modalContentClassName = classNames({
    [switchFrequencyStyles.modalContent]: isThreeOffersByFrequency,
  })

  const currentStepRef = useRef(currentStep)
  currentStepRef.current = currentStep

  useEffect(() => {
    return () => {
      if (cards[0]) sendModalClosedEvent(currentStepRef.current, cards[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const modalOfferLockData = isSecondStep
    ? {
        ...offerLockData,
        header: {
          ...offerLockData.header,
          title: offerLockData.header?.secondStepTitle || offerLockData.header?.title,
          subtitle: '',
        },
      }
    : offerLockData

  return (
    <ModalLock
      offerLockData={modalOfferLockData}
      size={isOneOfferByFrequency ? 'sm' : 'lg'}
      headingSize="lg"
      modalContentClassName={modalContentClassName}
    >
      <StepContentWrapper>
        <ChooseFrequencyLaterStepsContent offerLockData={offerLockData} viewer={viewer} />
      </StepContentWrapper>
    </ModalLock>
  )
}

ChooseFrequencyLaterRedesignLock.fragments = {
  offerLockData: gql`
    fragment ChooseFrequencyLaterRedesignLock_offerLockData on OfferLockData {
      header {
        title
        secondStepTitle
      }
      ...ModalLock_offerLockData
      ...PaywallLock_offerLockData
      ...ChooseFrequencyLaterSteps_offerLockData
    }
    ${ModalLock.fragments.offerLockData}
    ${PaywallLock.fragments.offerLockData}
    ${ChooseFrequencyLaterSteps.fragments.offerLockData}
  `,
  viewer: gql`
    fragment ChooseFrequencyLaterRedesignLock_viewer on Profile {
      ...ChooseFrequencyLaterSteps_viewer
    }
    ${ChooseFrequencyLaterSteps.fragments.viewer}
  `,
}
